import React from "react";
import { Collapse, Drawer } from "antd";
import {
  UserOutlined,
  SolutionOutlined,
  BankOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
} from "@ant-design/icons";

import { BriefcaseIcon, EducationIcon } from "./FilterIcons";
import { FilterPanel } from "./FilterDrawerPanels/FilterShared";
import {
  INITIAL,
  countActive,
  getVisibleLocationFilterCount,
  getVisibleSkillsExperienceFilterCount,
  PANELS,
} from "./FilterDrawerPanels/filterOptions";
import {
  useLocationSearch,
  usePmtCompanySearch,
  usePmtCitySearch,
  usePmtCountrySearch,
  usePmtRegionSearch,
  useTitleSearch,
  useSkillSearch,
} from "./FilterDrawerPanels/filterHooks";
import { IdentityPanel, JobTitlePanel, CompanyPanel, LocationPanel, SkillsPanel, EducationPanel, LanguagePanel } from "./FilterDrawerPanels";
import { VisaStatusPanel } from "./FilterDrawerPanels/PmtLegacyPanels";

import identifytag from '../../../../assets/icons/identifytag.svg'
import jobTitle from '../../../../assets/icons/jobTitle.svg'
import CompanyOrganization from '../../../../assets/icons/CompanyOrganization.svg'
import { ReactComponent as NlpIcon } from '../../../../assets/icons/nlpSearch.svg'
import { canUseSubscriptionFeatures } from '../../../../shared/billing';
import './FilterDrawer.css';

const FilterDrawer = ({
  open,
  onClose,
  onReset,
  onApply,
  values = INITIAL,
  onChange,
  hideCompanyPanel = false,
  hideJobTitleAdvancedFields = false,
  onNlpClick,
  nlpState,
  onNlpEdit,
  onNlpClear,
}) => {
  const isSkillRadarUser = canUseSubscriptionFeatures();
  const hideJobTitleRoleFields = hideJobTitleAdvancedFields || !isSkillRadarUser;
  const hideCompanyOrganizationPanel = hideCompanyPanel;
  const locMain = useLocationSearch();
  const locCurrWork = useLocationSearch();
  const locPastWork = useLocationSearch();
  const locRemote = useLocationSearch();
  const pmtCountrySearch = usePmtCountrySearch();
  const pmtRegionSearch = usePmtRegionSearch();
  const pmtCitySearch = usePmtCitySearch();
  const pmtCompanySearch = usePmtCompanySearch();
  const titleInc = useTitleSearch();
  const titleExc = useTitleSearch();
  const skillSearch = useSkillSearch();
  const set = (key) => (val) => onChange?.({ ...values, [key]: val });
  const setMany = (updates) => onChange?.({ ...values, ...updates });
  const setE = (key) => (e) => onChange?.({ ...values, [key]: e.target.value });

  const handleReset = () => {
    onChange?.(INITIAL);
    onReset?.();
  };
  const handleApply = () => onApply?.(values);

  const badges = {
    identity: countActive(values.name, values.keyword),
    jobTitle: countActive(values.jobTitleInclude, values.jobTitleExclude, values.jobFunction, values.seniority),
    company: countActive(values.companyInclude, values.companyExclude, values.domain, values.industry, values.companySize),
    location: getVisibleLocationFilterCount(values),
    skills: getVisibleSkillsExperienceFilterCount(values, {
      useLegacyExperience: !isSkillRadarUser,
    }),
    skillListingType: countActive(values.skillListingType),
    budget: countActive(values.budgetMin, values.budgetMax, values.budgetType),
    visaStatus: countActive(values.visaStatusId),
    education: countActive(values.school),
    language: countActive(values.language),
  };

  const defaultActiveKeys = [
    PANELS.IDENTITY.key,
    PANELS.JOB_TITLE.key,
    // PANELS.LOCATION.key,
    // PANELS.SKILLS.key,
    // PANELS.LANGUAGE.key,
  ];

  if (!hideCompanyOrganizationPanel) {
    defaultActiveKeys.splice(2, 0, PANELS.COMPANY.key);
  }

  return (
    <Drawer
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Filter</span>
          <span
            onClick={onClose}
            style={{
              cursor: "pointer", color: "var(--Color-Neutral-Primary, #1B1F25)"
            }}
          >
            ✕
          </span>
        </div>
      }
      placement="right"
      width="min(100vw, 600px)"
      onClose={onClose}
      closable={false}
      open={open}
      className="qp-filter-drawer"
      bodyStyle={{ padding: "0 12px" }}
      footer={
        <div className="qp-filter-drawer-footer">
          <button className="qp-filter-reset-btn" type="button" onClick={handleReset}>
            Reset
          </button>
          <button className="qp-filter-apply-btn" type="button" onClick={handleApply}>
            Apply Filter
          </button>
        </div>
      }
    >
      {onNlpClick && (
        nlpState?.description ? (
          <div style={{
            background: '#f0f5ff',
            border: '1px solid #d6e4ff',
            borderRadius: 8,
            padding: '12px 16px',
            margin: '12px 0 4px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontWeight: 600, fontSize: 14, color: '#1B1F25' }}>
              <NlpIcon style={{ color: '#1890ff', flexShrink: 0 }} />
              Natural Language Search
            </div>
            <p style={{
              fontSize: 12, color: '#595959', marginBottom: 10,
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {nlpState.description}
            </p>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <button type="button" onClick={() => { onClose?.(); onNlpEdit?.(); }} style={{ border: '1px solid #d9d9d9', borderRadius: 4, background: '#fff', padding: '4px 12px', cursor: 'pointer', fontSize: 12, color: '#1B1F25' }}>
                Edit Search
              </button>
              <button type="button" onClick={onNlpClear} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: '#1B1F25' }}>
                Clear
              </button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => { onClose?.(); onNlpClick(); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              background: '#f0f5ff',
              border: '1px solid #d6e4ff',
              borderRadius: 8,
              padding: '12px 16px',
              cursor: 'pointer',
              margin: '12px 0 4px',
            }}
          >
            <NlpIcon style={{ color: '#1890ff', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#1B1F25' }}>Natural Language Search</div>
              <div style={{ fontSize: 12, color: '#595959', marginTop: 2 }}>Describe nice-to-haves in your own words.</div>
            </div>
          </div>
        )
      )}

      <Collapse
        ghost
        expandIconPosition="end"
        className="qp-filter-collapse"
        defaultActiveKey={defaultActiveKeys}
      >

        {!isSkillRadarUser && (
          <FilterPanel key={PANELS.IDENTITY.key} panelKey={PANELS.IDENTITY.key} icon={<img src={identifytag} alt="" />} title={PANELS.IDENTITY.title} badge={badges.identity}>
            <IdentityPanel
              values={{ name: values.name }}
              setE={setE}
            />
          </FilterPanel>
        )}

        <FilterPanel key={PANELS.JOB_TITLE.key} panelKey={PANELS.JOB_TITLE.key} icon={<img src={jobTitle} alt="" />} title={PANELS.JOB_TITLE.title} badge={badges.jobTitle}>
          <JobTitlePanel
            values={{
              jobTitleInclude: values.jobTitleInclude,
              jobTitleExclude: values.jobTitleExclude,
              currentTitlesOnly: values.currentTitlesOnly,
              includeRelatedTitles: values.includeRelatedTitles,
              // matchExperience: values.matchExperience,
              jobFunction: values.jobFunction,
              seniority: values.seniority,
            }}
            set={set}
            titleInc={titleInc}
            titleExc={titleExc}
            hideAdvancedFields={hideJobTitleRoleFields}
          />
        </FilterPanel>

        {!hideCompanyOrganizationPanel && (
          <FilterPanel key={PANELS.COMPANY.key} panelKey={PANELS.COMPANY.key} icon={<img src={CompanyOrganization} alt="" />} title={PANELS.COMPANY.title} badge={badges.company}>
            <CompanyPanel
              values={{
                companyInclude: values.companyInclude,
                companyExclude: values.companyExclude,
                companyCurrentOnly: values.companyCurrentOnly,
                companyIncludeRelated: values.companyIncludeRelated,
                domain: values.domain,
                industry: values.industry,
                companySize: values.companySize,
              }}
              set={set}
              setE={setE}
              hideAdvancedFields={!isSkillRadarUser}
              companySearch={!isSkillRadarUser ? pmtCompanySearch : undefined}
            />
          </FilterPanel>
        )}

        <FilterPanel key={PANELS.LOCATION.key} panelKey={PANELS.LOCATION.key} icon={<EnvironmentOutlined />} title={PANELS.LOCATION.title} badge={badges.location}>
          <LocationPanel
            values={{
              location: values.location,
              cityId: values.cityId,
              cityName: values.cityName,
              regionId: values.regionId,
              regionName: values.regionName,
              countryId: values.countryId,
              countryName: values.countryName,
              currentWorkLocation: values.currentWorkLocation,
              pastWorkLocation: values.pastWorkLocation,
              remoteLocation: values.remoteLocation,
            }}
            set={set}
            setMany={setMany}
            locMain={locMain}
            locCurrWork={locCurrWork}
            locPastWork={locPastWork}
            locRemote={locRemote}
            hideWorkLocationFields={!isSkillRadarUser}
            usePmtLocationFields={!isSkillRadarUser}
            countrySearch={pmtCountrySearch}
            regionSearch={pmtRegionSearch}
            citySearch={pmtCitySearch}
          />
        </FilterPanel>

        <FilterPanel key={PANELS.SKILLS.key} panelKey={PANELS.SKILLS.key} icon={<BriefcaseIcon />} title={PANELS.SKILLS.title} badge={badges.skills}>
          <SkillsPanel
            values={{
              skill: values.skill,
              yearsOfExp: values.yearsOfExp,
              yearsOfExpCurrentRole: values.yearsOfExpCurrentRole,
              experienceLevel: values.experienceLevel,
              skillListingType: values.skillListingType,
              budgetMin: values.budgetMin,
              budgetMax: values.budgetMax,
              budgetType: values.budgetType,
            }}
            set={set}
            skillSearch={skillSearch}
            useLegacyExperience={!isSkillRadarUser}
          />
        </FilterPanel>

        {!isSkillRadarUser && (
          <FilterPanel panelKey="visaStatus" icon={<GlobalOutlined />} title="Visa Status" badge={badges.visaStatus}>
            <VisaStatusPanel
              values={{ visaStatusId: values.visaStatusId }}
              set={set}
            />
          </FilterPanel>
        )}
        {isSkillRadarUser && (
          <FilterPanel
            key={PANELS.EDUCATION.key}
            panelKey={PANELS.EDUCATION.key}
            icon={<EducationIcon />}
            title={PANELS.EDUCATION.title}
            badge={badges.education}
          >
            <EducationPanel
              values={{ school: values.school }}
              set={set}
            />
          </FilterPanel>
        )}

        {isSkillRadarUser && (
          <FilterPanel
            key={PANELS.LANGUAGE.key}
            panelKey={PANELS.LANGUAGE.key}
            icon={<GlobalOutlined />}
            title={PANELS.LANGUAGE.title}
            badge={badges.language}
          >
            <LanguagePanel
              values={{ language: values.language }}
              set={set}
            />
          </FilterPanel>
        )}
      </Collapse>
    </Drawer>
  );
};

export default FilterDrawer;
